import { useEffect, useCallback } from 'react';
import { mqttClient, MQTTConfig, TOPICS } from './client';
import { MQTTEvent } from '../types/event';
import { useAuthStore } from '../store/authSlice';

// Global flag to prevent multiple initializations (survives React StrictMode)
let mqttInitialized = false;

/**
 * Main MQTT hook - initializes connection when user is authenticated
 * Should be called once in App.tsx
 */
export function useMQTTConnection() {
  const { user, token } = useAuthStore();

  useEffect(() => {
    if (!user || !token) {
      // Only disconnect if we were initialized
      if (mqttInitialized) {
        mqttClient.disconnect();
        mqttInitialized = false;
      }
      return;
    }

    // Skip if already initialized (global check)
    if (mqttInitialized) {
      console.log('[useMQTTConnection] Already initialized, skipping');
      return;
    }
    
    mqttInitialized = true;

    // Generate unique device ID for this browser session
    const deviceId = sessionStorage.getItem('mqtt_device_id') || crypto.randomUUID().slice(0, 8);
    sessionStorage.setItem('mqtt_device_id', deviceId);
    
    const config: MQTTConfig = {
      brokerUrl: import.meta.env.VITE_MQTT_URL || 'ws://localhost:2083/mqtt',
      clientId: `miniapp_${user.id}_${deviceId}`,
      username: user.id,
      password: token,
    };

    mqttClient.connect(config);
    console.log('[useMQTTConnection] Initialized, user:', user.id);

    // No cleanup - connection persists
  }, [user, token]);

  return {
    isConnected: mqttClient.isConnected(),
    userId: mqttClient.userId,
  };
}

/**
 * Hook for subscribing to specific topic
 */
export function useMQTTSubscription(
  topic: string | null,
  handler: (event: MQTTEvent) => void,
  deps: React.DependencyList = []
) {
  const memoizedHandler = useCallback(handler, deps);

  useEffect(() => {
    if (!topic) return;

    const unsubscribe = mqttClient.subscribe(topic, memoizedHandler);
    console.log(`[useMQTTSubscription] Subscribed to ${topic}`);

    return () => {
      unsubscribe();
      console.log(`[useMQTTSubscription] Unsubscribed from ${topic}`);
    };
  }, [topic, memoizedHandler]);
}

/**
 * Hook for subscribing to all MQTT messages
 */
export function useMQTTMessages(
  handler: (topic: string, event: MQTTEvent) => void,
  deps: React.DependencyList = []
) {
  const memoizedHandler = useCallback(handler, deps);

  useEffect(() => {
    const unsubscribe = mqttClient.onMessage(memoizedHandler);
    return unsubscribe;
  }, [memoizedHandler]);
}

/**
 * Hook for subscribing to user events
 */
export function useUserEvents(handler: (event: MQTTEvent) => void, deps: React.DependencyList = []) {
  const { user } = useAuthStore();
  const topic = user ? TOPICS.userEvents(user.id) : null;
  useMQTTSubscription(topic, handler, deps);
}



/**
 * Hook for subscribing to group events
 */
export function useGroupEvents(
  groupId: string | null,
  handler: (event: MQTTEvent) => void,
  deps: React.DependencyList = []
) {
  const topic = groupId ? TOPICS.groupEvents(groupId) : null;
  useMQTTSubscription(topic, handler, deps);
}

/**
 * Hook for subscribing to broadcast events
 */
export function useBroadcastEvents(
  eventType: string,
  handler: (event: MQTTEvent) => void,
  deps: React.DependencyList = []
) {
  const topic = TOPICS.broadcast(eventType);
  useMQTTSubscription(topic, handler, deps);
}

/**
 * Hook for subscribing to plugin events
 */
export function usePluginEvents(
  pluginId: string,
  eventType: string,
  handler: (event: MQTTEvent) => void,
  deps: React.DependencyList = []
) {
  const topic = TOPICS.pluginEvents(pluginId, eventType);
  useMQTTSubscription(topic, handler, deps);
}

/**
 * Hook for subscribing to system monitoring events
 */
export function useMonitoringEvents(
  handler: (event: MQTTEvent) => void,
  deps: React.DependencyList = []
) {
  const topic = TOPICS.systemMonitoring();
  useMQTTSubscription(topic, handler, deps);
}

/**
 * Hook for publishing messages
 */
export function useMQTTPublish() {
  const publish = useCallback((topic: string, payload: unknown) => {
    mqttClient.publish(topic, payload);
  }, []);

  return publish;
}

/**
 * Hook for sync command-response pattern
 */
export function useMQTTCommand() {
  const command = useCallback(async <T = unknown>(
    commandTopic: string,
    responseTopic: string,
    payload: unknown,
    timeoutMs = 5000
  ): Promise<T> => {
    return mqttClient.command<T>(commandTopic, responseTopic, payload, timeoutMs);
  }, []);

  return command;
}

// Re-export for convenience
export { mqttClient, TOPICS };
